import { createTRPCReact } from '@trpc/react-query';
import { TRPCLink, httpBatchLink } from '@trpc/client';
import type { AppRouter } from 'backend';
import { observable } from '@trpc/server/observable';
import { APIError } from '@mightybyte/business';
import { MB_accessTokenUtils } from '@mightybyte/rnw2.utils.access-token-utils';
import { forceRefreshToken, getAccessToken } from './auth';

export const trpc = createTRPCReact<AppRouter>();

/**
 * On a 401 tRPC error, force-refresh the token once and retry.
 * Only redirects to /login if the refresh itself fails or the retry also 401s.
 */
const tokenRefreshLink: TRPCLink<AppRouter> = () => {
    return ({ next, op }) => {
        return observable((observer) => {
            let retried = false;

            const attempt = () => {
                next(op).subscribe({
                    next: (value) => observer.next(value),
                    complete: () => observer.complete(),
                    error: async (error) => {
                        const errCode =
                            error?.data?.error?.code ??
                            error?.shape?.data?.code;
                        const is401 =
                            errCode === APIError.invalidAccessToken.code ||
                            errCode === APIError.expiredAccessToken.code;

                        if (is401 && !retried) {
                            retried = true;
                            try {
                                await forceRefreshToken();
                                attempt();
                            } catch {
                                await MB_accessTokenUtils.removeAccessToken();
                                window.location.href = '/login';
                                observer.error(error);
                            }
                        } else if (
                            errCode === APIError.invalidRefreshToken.code
                        ) {
                            await MB_accessTokenUtils.removeAccessToken();
                            window.location.href = '/login';
                            observer.error(error);
                        } else {
                            observer.error(error);
                        }
                    },
                });
            };

            attempt();
        });
    };
};

export const trpcClient = trpc.createClient({
    links: [
        tokenRefreshLink,
        httpBatchLink({
            url: `${import.meta.env.VITE_API_URL}/trpc`,
            async fetch(url, options) {
                const authHeader = await getAccessToken();
                const headers = options?.headers
                    ? new Headers(options.headers)
                    : new Headers();

                if (authHeader) {
                    headers.set('Authorization', authHeader);
                }

                return fetch(url, {
                    ...options,
                    headers,
                    credentials: 'include',
                });
            },
        }),
    ],
});
